import React, {useEffect, useState} from 'react';
import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {
  Expressions,
  getMeta,
  withSystem,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  TRecord,
  TText,
  TNumber,
  TList,
  ENumber,
  EText,
  type Expression,
} from '@packages/systems/wfdl-ast';
import PropertyList, {
  PropertyListItem,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import {COMMERCE_DEFAULT_COPY} from '@packages/systems/commerce/constants';
import {
  styled,
  emotionStyled,
  styledDiv,
  tokens,
} from '@packages/utilities/styled';
import {
  CLOSED_STATE,
  OPENED_STATE,
  DeprecatedPopoverBridge,
} from '@packages/systems/design-system/PopoverBridge';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import Textarea from '@packages/systems/design-system/Textarea';
import Button from '@packages/systems/design-system/Button';
import {Note, NoteLink} from '@packages/systems/spring';
import {TransformRotate as RotateIcon} from '@packages/systems/ui/icons';
import {createIntegratedRootAlias} from '@packages/utilities/integrated-types';
import {
  type ContextualExpression,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';

const ERROR_PATH: ExpressionPath = [{in: 'Record', at: 'texts'}];
const INDEX_PATH: ExpressionPath = [...TEMP_PATH, {in: 'Record', at: 'index'}];
const CATEGORY_PATH: ExpressionPath = [{in: 'Record', at: 'category'}];
const NOTES = {
  ORDER_MINIMUM_ERROR: {
    copy: "You can customize this message with the exact minimum based on your Stripe account's settlement currency.",
    cta: {
      copy: 'Go to Stripe docs',
      link: 'https://stripe.com/docs/currencies#minimum-and-maximum-charge-amounts',
    },
  },
} as const;

const EditTextHeader = styledDiv(({theme}) => ({
  padding: '6px 8px',
  fontFamily: theme.typography.subheading.fontFamily,
  fontSize: theme.typography.subheading.fontSize,
  fontWeight: theme.typography.subheading.fontWeight,
  borderBottomStyle: 'solid',
  borderBottomWidth: theme.control.borderWidth,
  borderBottomColor: theme.colors.backgroundDeepAlt,
}));

const Category = emotionStyled('div')({
  marginTop: '6px',
  marginBottom: '6px',
  '&:first-of-type': {
    marginTop: '0px',
  },
});

const StyledNote = styled(Note)(({theme: {gutters}}) => ({
  marginTop: gutters.x8,
}));

const getTextsList = (expr: Expression) => {
  const textsExpr = Expressions.getIn(expr, ERROR_PATH);
  if (textsExpr === undefined) return [];
  return Expressions.getList(textsExpr) || [];
};

const getCategoryMap = (exprs: Array<Expression>) => {
  const categories = new Map();
  exprs.forEach((expr, index) => {
    const categoryExpr = Expressions.getIn(expr, CATEGORY_PATH);
    if (!categoryExpr) return;
    const category = Expressions.getText(categoryExpr);
    if (!category) return;
    if (categories.has(category)) {
      const categoryList = categories.get(category);
      if (!categoryList) throw Error('expected category');
      categories.set(category, categoryList.concat({expr, index}));
    } else {
      categories.set(category, [{expr, index}]);
    }
  });

  return categories;
};

const getExprIndex = (expr: Expression) => {
  const indexExpr = Expressions.getIn(expr, INDEX_PATH);
  return indexExpr ? (Expressions.getNumber(indexExpr) ?? 0) : 0;
};

const setIndex = (expr: Expression, index: number) =>
  Expressions.setIn(expr, INDEX_PATH, ENumber(index));

const getName = (item: Expression): string => {
  const name = Expressions.getRecord(item)?.name;
  if (!name) return '';

  return Expressions.getText(name) || '';
};

const getTextId = (item: Expression): string => {
  const textId = Expressions.getRecord(item)?.id;
  if (!textId) return '';

  return Expressions.getText(textId) || '';
};

const Island = emotionStyled('div')({
  padding: tokens.space[2],
  width: rightSidebarWidth,
});

const StyledTextArea = styled(Textarea)(({theme}) => ({
  height: theme.gutters.x20 * 5 + theme.gutters.x12,
  resize: 'none',
  padding: theme.gutters.x4,
  fontSize: '12px',
  marginBottom: '8px',
}));

const RestoreDefaultButton = ({
  onClick,
  tooltipContent,
}: {
  onClick?: null | (() => void);
  tooltipContent: string;
}) => {
  if (onClick == null) return null;

  return (
    <TooltipTrigger content={tooltipContent} copy alternate placement="bottom">
      <Button
        structure="ghost"
        onClick={onClick}
        compact
        style={{
          width: '100%',
          marginTop: 8,
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        <RotateIcon
          style={{
            display: 'flex',
            marginRight: 8,
          }}
        />
        Restore default
      </Button>
    </TooltipTrigger>
  );
};

const EditTextArea = ({
  defaultCopy,
  commit,
  initialValue,
  'data-automation-id': dataAutomationId,
}: {
  defaultCopy: string;
  commit: (arg1: string) => void;
  initialValue: string;
  'data-automation-id'?: string;
}) => {
  const [text, setText] = useState(initialValue);
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setText(e.target.value);

  useEffect(() => {
    commit(text);
  }, [commit, text]);

  return (
    <>
      <StyledTextArea
        text={text}
        onChange={onChange}
        {...(dataAutomationId && {'data-automation-id': dataAutomationId})}
      />
      {text !== defaultCopy && (
        <RestoreDefaultButton
          onClick={() => {
            setText(defaultCopy);
          }}
          tooltipContent={defaultCopy}
        />
      )}
    </>
  );
};

const Render = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1: any) => void;
}) => {
  const [indexState, setStateIndex] = useState(-1);
  const [hoveredItem, setHoveredItem] = useState(-1);

  useEffect(() => {
    if (indexState !== -1) {
      dispatch(updateExpressionAction(setIndex(strippedExpr, indexState)));
    }
  }, [dispatch, expr, indexState]);

  const {env} = useExpressionEditorImplementationContext();

  const type = getAnn(expr)?.type;
  const maybeMeta = type ? getMeta(type) : {};
  const meta = maybeMeta || {};
  const textStates = meta.textStates || {};

  function filterTexts() {
    return textsList.filter((text) => {
      const textId = getTextId(text);
      const textState = textStates[textId];
      if (
        !textState ||
        !textState.beta ||
        env.isEnabled({type: 'betaFlag', key: textState.beta})
      ) {
        return true;
      }
      return false;
    });
  }

  const strippedExpr = stripAll(expr);
  const textsList = getTextsList(strippedExpr);
  const texts = filterTexts();
  const categoryMap = getCategoryMap(texts);
  const indexExpr = getExprIndex(strippedExpr);

  const renderListItem = (listItem: {item: Expression; id: string}) => {
    const idx = Number(listItem.id);
    const isSelected = indexExpr === idx;
    const isOpen = indexState === idx;
    const textId = getTextId(listItem.item);
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly ORDER_MINIMUM_ERROR: { readonly copy: "You can customize this message with the exact minimum based on your Stripe account's settlement currency."; readonly cta: { readonly copy: "Go to Stripe docs"; readonly link: "https://stripe.com/docs/currencies#minimum-and-maximum-charge-amounts"; }; }; }'.
    const note = textId in NOTES ? NOTES[textId] : null;

    const Input = () => {
      const indexCopyPath: ExpressionPath = [
        ...ERROR_PATH,
        {in: 'List', index: idx},
        {in: 'Record', at: 'copy'},
      ];

      const copyExpr = Expressions.getIn(strippedExpr, indexCopyPath);
      const copy = copyExpr ? (Expressions.getText(copyExpr) ?? '') : '';

      const setCopy = (textValue: string) => {
        const newExpr = Expressions.setIn(
          strippedExpr,
          indexCopyPath,
          EText(textValue)
        );
        dispatch(updateExpressionAction(newExpr));
      };

      return (
        <div data-automation-id={`${textId}-error-message-popover`}>
          <EditTextHeader>Edit text</EditTextHeader>
          <Island>
            {copyExpr && (
              <EditTextArea
                data-automation-id={`${textId}-error-message-textarea`}
                initialValue={copy}
                commit={setCopy}
                // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly INFO_ERROR: string; readonly SHIPPING_ERROR: string; readonly ORDER_EXTRAS_ERROR: string; readonly PRICING_ERROR: string; readonly PRODUCT_ERROR: string; readonly PAYMENT_ERROR: string; ... 20 more ...; readonly SELECT_ALL_OPTIONS: "Please select an option in each set."; }'.
                defaultCopy={COMMERCE_DEFAULT_COPY[textId]}
              />
            )}
            {note ? (
              <StyledNote hasIcon={false} colorVariant="default">
                {note.copy}
                {note.cta ? (
                  <NoteLink isExternal target="_blank" href={note.cta.link}>
                    {note.cta.copy}
                  </NoteLink>
                ) : null}
              </StyledNote>
            ) : null}
          </Island>
        </div>
      );
    };

    return (
      <DeprecatedPopoverBridge
        // @ts-expect-error - TS2769 - No overload matches this call.
        key={idx}
        onChange={(popoverState) => {
          if (popoverState.open === false) {
            setStateIndex(-1);
          }
        }}
        state={isOpen ? OPENED_STATE : CLOSED_STATE}
        renderPopoverContent={Input}
        showArrow={false}
        showMask="TRANSPARENT"
        metricsIdentifier="multi-text-edit-type-popover"
      >
        {({Anchor}) => (
          <Anchor asChild={false}>
            {(ref: React.RefObject<any>) => (
              <PropertyListItem
                _ref={ref}
                showIconsByDefault
                checkedIndicator="radio"
                isSelected={isSelected}
                editable
                // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'number' and 'string' have no overlap.
                hover={hoveredItem === listItem.id}
                id={String(listItem.id)}
                key={listItem.id}
                label={getName(listItem.item)}
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<number>'.
                onMouseOver={() => setHoveredItem(listItem.id)}
                onMouseOut={() => setHoveredItem(-1)}
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<number>'.
                onFocus={() => setHoveredItem(listItem.id)}
                onBlur={() => setHoveredItem(-1)}
                onEditButtonClicked={() => {
                  setStateIndex(Number(listItem.id));
                }}
                onClick={() => {
                  dispatch(
                    updateExpressionAction(
                      setIndex(strippedExpr, Number(listItem.id))
                    )
                  );
                }}
              />
            )}
          </Anchor>
        )}
      </DeprecatedPopoverBridge>
    );
  };

  return (
    <>
      {[...categoryMap].map(([category, categoryList]: [any, any]) => (
        <React.Fragment key={category}>
          <Category>{category}</Category>
          <PropertyList
            disabled={false}
            // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
            items={categoryList.map((item) => ({
              item: item.expr,
              id: String(item.index),
            }))}
            renderItem={renderListItem}
          />
        </React.Fragment>
      ))}
    </>
  );
};

const fields = {
  temp: TRecord({
    index: TNumber,
  }),
  texts: TList(
    TRecord({
      id: withSystem({private: true}, TText),
      name: withSystem({private: true}, TText),
      copy: withSystem({private: true}, TText),
      category: withSystem({private: true}, TText),
    })
  ),
} as const;

export const MultiTextEditType = createIntegratedRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: Render,
});

export const AddToCartButtonMultiTextEditType = createIntegratedRootAlias({
  params: ['extraAtomFields'],
  fields: {
    ...fields,
    attr: TRecord({type: withSystem({private: true}, TText)}),
  },
  render: Render,
});
